import { useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { voiceActorRows } from '@/mocks/voiceActors';

const siteUrl = 'https://starvoice3.github.io';

export default function VoiceActors() {
  const [keyword, setKeyword] = useState('');

  const list = voiceActorRows.filter((row) => {
    const kw = keyword.trim();
    return (
      !kw ||
      row.name.includes(kw) ||
      row.romaji.toLowerCase().includes(kw.toLowerCase()) ||
      row.role.includes(kw)
    );
  });

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '守护甜心动漫声优表',
      url: `${siteUrl}/voice-actors`,
      description: '守护甜心动漫配音演员与角色对照表，含主要角色的幕后声优信息。',
      inLanguage: 'zh-CN',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: voiceActorRows.map((r, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${r.name}（配音 ${r.role}）`,
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '声优表', item: `${siteUrl}/voice-actors` },
      ],
    },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <JsonLd data={schema} />
      <nav aria-label="面包屑" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li aria-current="page" className="font-medium text-foreground-800">
            声优表
          </li>
        </ol>
      </nav>

      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">
            守护甜心动漫声优表
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">
            主要角色的配音演员与角色一一对照。想确认某位角色的幕后声音是谁，直接看这张表就好。
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <i
            className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索声优 / 角色"
            aria-label="搜索声优"
            className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-background-200/80 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <caption className="sr-only">守护甜心动漫主要角色配音演员对照表</caption>
            <thead>
              <tr className="bg-background-100 text-foreground-500">
                <th scope="col" className="px-5 py-3.5 font-semibold">配音演员</th>
                <th scope="col" className="px-5 py-3.5 font-semibold">罗马音</th>
                <th scope="col" className="px-5 py-3.5 font-semibold">饰演角色</th>
                <th scope="col" className="px-5 py-3.5 font-semibold">备注</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-background-200/70 transition-colors hover:bg-background-100/60"
                >
                  <td className="px-5 py-3.5 font-heading font-bold text-foreground-900">{row.name}</td>
                  <td className="px-5 py-3.5 text-foreground-500">{row.romaji}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                      {row.role}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-foreground-600">{row.note}</td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-foreground-500">
                    没有找到匹配的声优，换个关键词试试吧。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-background-100/70 p-6 text-sm leading-7 text-foreground-600">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
          <i className="ri-information-line text-accent-600" aria-hidden="true"></i>
          关于声优表的小说明
        </h2>
        <p className="mt-2">
          本页守护甜心动漫声优表收录的是主要角色及守护甜心的配音演员。动画中部分角色、路人与特别篇角色可能由其他声优出演，信息以作品实际播放阵容为准。想查看角色对应的详细背景故事，可前往
          <Link to="/characters" className="mx-1 font-medium text-primary-600 hover:underline">
            角色大全
          </Link>
          按名检索。
        </p>
      </div>
    </div>
  );
}